import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  isLiveCredentialRequest,
  listCredentialRequests,
  type CredentialRequestDto,
} from '../services/credentialRequestsApi';

/**
 * Credential requests raised on one RepairShopr ticket.
 *
 * `liveRequest` is what the Credentials button reads: a request that has been
 * revoked, or retired by the idle purge, must stop standing in the way of
 * raising a new one. Dead requests stay in `requests` as history.
 *
 * Pass `rsTicketId` as `''` or `'0'` for a work item with no RepairShopr
 * ticket — the hook then fetches nothing, because a credential request has
 * nothing to bind to.
 */
export function useCredentialRequests(rsTicketId: string) {
  const [requests, setRequests] = useState<CredentialRequestDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const enabled = Boolean(rsTicketId) && rsTicketId !== '0';

  useEffect(() => {
    if (!enabled) {
      setRequests([]);
      return;
    }

    const controller = new AbortController();
    setIsLoading(true);
    setError(null);

    listCredentialRequests(rsTicketId, controller.signal)
      .then((items) => setRequests(items))
      .catch((err) => {
        if (err instanceof Error && err.name === 'AbortError') return;
        setError(err instanceof Error ? err.message : 'Failed to load credential requests');
      })
      .finally(() => setIsLoading(false));

    return () => controller.abort();
  }, [rsTicketId, enabled, reloadKey]);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const liveRequest = useMemo(
    () => requests.find(isLiveCredentialRequest) ?? null,
    [requests],
  );

  return { requests, liveRequest, isLoading, error, refresh };
}
